import { http, publicHttp } from "../Guard";
import {
  AuthInput,
  AuthToken,
  LockUserInput,
  RefreshToken,
  RefreshTokenInput,
  RegisterCompanyEmployee,
  RegisterCompanyEmployeeInput,
  RegisterCompanyEmployeesPreview,
  RegisterCompanyUser,
  RegisterCompanyUserFileInput,
  RegisterCompanyUserInput,
  RegisterCompanyUsersPreview,
  RegisterFileInput,
  RegisterFilePreviewInput,
  RegisterUserInput,
  ResetPasswordCodeInput,
  ResetPasswordInput,
  UnlockUserInput,
  User,
  UserInput,
} from "./auth.default";

export function authLogin(input: AuthInput): Promise<AuthToken> {
  // publicHttp = no interceptor: login must never carry a stale bearer or enter the refresh flow.
  return publicHttp.post("/api/Auth/Login", input).then(({ data }) => data);
}

export function authLogout(): Promise<void> {
  return http.post("/api/Auth/Logout").then(({ data }) => data);
}

export function getProfile(): Promise<User> {
  return http.get("/api/Auth/Profile").then(({ data }) => data);
}

export function updateProfile(input: UserInput): Promise<void> {
  return http.post("/api/Auth/Profile", input);
}

export function getAvatar(): Promise<string> {
  return http
    .get("/api/Auth/Profile/Avatar", {
      responseType: "blob",
    })
    .then(({ data }) => URL.createObjectURL(data));
}

export function uploadAvatar(file: File): Promise<any> {
  const formData = new FormData();
  formData.append("avatarFile", file);
  return http
    .post("/api/Auth/Profile/Avatar", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(({ data }) => data);
}

export function refreshToken(input: RefreshTokenInput): Promise<RefreshToken> {
  return publicHttp
    .post("/api/Auth/RefreshToken", input, {
      headers: {
        Authorization: `Bearer ${input.token}`,
      },
    })
    .then(({ data }) => data);
}

export function registerUser(input: RegisterUserInput): Promise<void> {
  return http.post("/api/Auth/Register", input);
}

export function resetPasswordCode(
  input: ResetPasswordCodeInput,
): Promise<string> {
  return http
    .get("/api/Auth/PasswordResetCode", {
      params: input,
    })
    .then(({ data }) => data);
}

export function resetPassword(input: ResetPasswordInput) {
  return http.post("/api/Auth/ResetPassword", input).then(({ data }) => data);
}

export function resetPasswordForUser(userUid: string): Promise<void> {
  return http
    .post(`/api/Auth/ResetPasswordForUser/${userUid}`)
    .then(({ data }) => data);
}

export function confirmEmail(input: ResetPasswordInput) {
  return http.post("/api/Auth/ConfirmEmail", input).then(({ data }) => data);
}

// User

export function registerCompanyUser(
  input: RegisterCompanyUserInput,
): Promise<RegisterCompanyUser> {
  return http
    .post("/api/Auth/RegisterForCompany", input)
    .then(({ data }) => data);
}

export function registerCompanyUsersFile(
  input: RegisterCompanyUserFileInput,
): Promise<RegisterCompanyUser> {
  const formData = new FormData();
  formData.append("xmlFile", input.file);
  return http
    .post("/api/Auth/RegisterForCompanyWithFile", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        companyUid: input.companyUid,
      },
    })
    .then(({ data }) => data);
}

export function getRegisterUsersTemplate() {
  return http
    .get("/api/Auth/RegisterForCompanyWithFile/Template", {
      responseType: "blob",
    })
    .then(({ data }) => {
      try {
        const link = document.createElement("a");
        const urlBlob = window.URL.createObjectURL(new Blob([data]));
        try {
          link.href = urlBlob;
          link.setAttribute("download", "company-register-users-template.xlsx");
          document.body.appendChild(link);
          link.click();
        } catch {
          throw "Failed to download company register users template.";
        } finally {
          link.remove();
          window.URL.revokeObjectURL(urlBlob);
        }
      } catch {
        throw "Failed to download company register users template.";
      }
    });
}

export function registerForCompanyWithFilePreview(
  input: RegisterFilePreviewInput,
): Promise<RegisterCompanyUsersPreview> {
  const formData = new FormData();
  formData.append("xmlFile", input.file);
  return http
    .post("/api/Auth/RegisterForCompanyWithFilePreview", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        companyUid: input.companyUid,
      },
    })
    .then(({ data }) => data);
}

// Employee

export function registerCompanyEmployee(
  input: RegisterCompanyEmployeeInput,
): Promise<RegisterCompanyEmployee> {
  return http
    .post("/api/Auth/RegisterEmployeeForCompany", input)
    .then(({ data }) => data);
}

export function registerCompanyEmployeesFile(
  input: RegisterFileInput,
): Promise<RegisterCompanyEmployee> {
  const formData = new FormData();
  formData.append("xmlFile", input.file);
  return http
    .post(`/api/Auth/RegisterEmployeeForCompanyWithFile`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        companyUid: input.companyUid,
      },
    })
    .then(({ data }) => data);
}

export function getRegisterEmployeesTemplate() {
  return http
    .get("/api/Auth/RegisterEmployeeForCompanyWithFile/Template", {
      responseType: "blob",
    })
    .then(({ data }) => {
      try {
        const link = document.createElement("a");
        const urlBlob = window.URL.createObjectURL(new Blob([data]));
        try {
          link.href = urlBlob;
          link.setAttribute(
            "download",
            "company-register-employees-template.xlsx",
          );
          document.body.appendChild(link);
          link.click();
        } catch {
          throw "Failed to download company register employees template.";
        } finally {
          link.remove();
          window.URL.revokeObjectURL(urlBlob);
        }
      } catch {
        throw "Failed to download company register employees template.";
      }
    });
}

export function registerEmployeesForCompanyWithFilePreview(
  input: RegisterFilePreviewInput,
): Promise<RegisterCompanyEmployeesPreview> {
  const formData = new FormData();
  formData.append("xmlFile", input.file);
  return http
    .post("/api/Auth/RegisterEmployeeForCompanyWithFilePreview", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        companyUid: input.companyUid,
      },
    })
    .then(({ data }) => data);
}

export function lockUser(input: LockUserInput) {
  return http
    .post(`/api/Auth/LockUser/${input.userUid}`, null, {
      params: {
        lockoutEnd: input.lockoutEnd,
      }
    })
    .then(({ data }) => data);
}

export function unlockUser(input: UnlockUserInput) {
  return http
    .post(`/api/Auth/UnlockUser/${input.userUid}`)
    .then(({ data }) => data);
}

export function initiateConfirmEmail(userUid: string) {
  return http
    .post(`/api/Auth/InitiateConfirmEmail/${userUid}`)
    .then(({ data }) => data);
}

export function initiateConfirmPhone(userUid: string) {
  return http
    .post(`/api/Auth/InitiateConfirmPhone/${userUid}`)
    .then(({ data }) => data);
}

export function initiateConfirmCompanyPhone(userUid: string) {
  return http
    .post(`/api/Auth/InitiateConfirmCompanyPhone/${userUid}`)
    .then(({ data }) => data);
}

